import React from 'react';
import { X } from 'lucide-react';

export interface FintsTanMethodOption {
  id: string;
  name?: string;
  zka_id?: string;
  tan_process?: string;
}

export interface FintsTanChallengeModalProps {
  open: boolean;
  /** z. B. „FinTS-TAN“ oder „Kontoauszug-Test“ */
  contextTitle?: string;
  decoupled: boolean;
  challengeText: string;
  challengeMediaB64: string | null;
  challengeMediaMime: string | null;
  challengeHhduc: string | null;
  bankMediumName: string | null;
  procedureHint: string;
  methodOptions: FintsTanMethodOption[];
  selectedMethod: string;
  onSelectedMethodChange: (value: string) => void;
  tanValue: string;
  onTanValueChange: (value: string) => void;
  onSubmit: () => void;
  /** Dialog abbrechen (lokale Session ohne Server-Roundtrip beenden) */
  onAbort?: () => void;
  submitting: boolean;
}

/**
 * Modal für FinTS-TAN/Challenges (QR-/photoTAN-Bild, chipTAN-Hinweise, Freigabe pushTAN).
 * Soll bei jedem Backend-„tan_required“ erscheinen, bis die TAN gesendet wurde oder abgebrochen wird.
 */
const FintsTanChallengeModal: React.FC<FintsTanChallengeModalProps> = ({
  open,
  contextTitle = 'FinTS',
  decoupled,
  challengeText,
  challengeMediaB64,
  challengeMediaMime,
  challengeHhduc,
  bankMediumName,
  procedureHint,
  methodOptions,
  selectedMethod,
  onSelectedMethodChange,
  tanValue,
  onTanValueChange,
  onSubmit,
  onAbort,
  submitting
}) => {
  if (!open) return null;

  const hasQrImage = Boolean(challengeMediaB64 && challengeMediaMime?.startsWith('image/'));

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fints-tan-modal-title"
    >
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-lg bg-white shadow-xl border border-amber-100">
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-amber-100 bg-amber-50/95 px-4 py-3 backdrop-blur-sm">
          <div>
            <h2 id="fints-tan-modal-title" className="text-base font-semibold text-amber-950">
              {decoupled ? `${contextTitle}: Freigabe in der App` : `${contextTitle}: TAN erforderlich`}
            </h2>
            {(selectedMethod || methodOptions[0]?.id) && (
              <p className="mt-0.5 text-xs text-amber-900">
                Verfahren{' '}
                <span className="font-mono font-medium">{selectedMethod || methodOptions[0]?.id}</span>
              </p>
            )}
          </div>
          {onAbort && (
            <button
              type="button"
              onClick={() => !submitting && onAbort()}
              disabled={submitting}
              className="rounded-md p-1 text-amber-900 hover:bg-amber-100 disabled:opacity-40"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="space-y-4 p-4">
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{challengeText}</p>

          {bankMediumName && (
            <p className="text-xs text-gray-700">
              TAN-Medium (laut Bank): <span className="font-medium">{bankMediumName}</span>
            </p>
          )}

          {procedureHint ? (
            <p className="text-sm leading-snug text-amber-950 bg-amber-50 rounded-md px-3 py-2 border border-amber-100">
              {procedureHint}
            </p>
          ) : null}

          {methodOptions.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">TAN-Verfahren</label>
              <select
                value={selectedMethod}
                onChange={(e) => onSelectedMethodChange(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white disabled:bg-gray-100"
              >
                {methodOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id}
                    {m.name ? ` — ${m.name}` : ''}
                    {m.zka_id ? ` (${m.zka_id})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {hasQrImage && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium text-gray-700">
                QR-Code / Bild zur Freigabe (von der Bank)
              </p>
              <div className="flex justify-center overflow-auto max-h-[min(58vh,420px)]">
                <img
                  src={`data:${challengeMediaMime};base64,${challengeMediaB64}`}
                  alt="FinTS QR- oder Challenge-Grafik"
                  className="max-w-full object-contain rounded border border-gray-200 bg-white"
                />
              </div>
            </div>
          )}

          {challengeHhduc && (
            <p className="text-xs font-mono text-gray-800 break-all rounded bg-gray-100 px-2 py-2 border border-gray-200">
              {challengeHhduc}
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={tanValue}
              onChange={(e) => onTanValueChange(e.target.value)}
              disabled={decoupled || submitting}
              autoComplete="one-time-code"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
              placeholder={
                decoupled ? 'Bei PushTAN oft leer — dann „Status prüfen“' : 'TAN eingeben und senden'
              }
            />
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50 shrink-0"
            >
              {submitting ? 'Wird gesendet…' : decoupled ? 'Status prüfen / weiter' : 'TAN senden'}
            </button>
          </div>

          {onAbort && (
            <button
              type="button"
              onClick={() => !submitting && onAbort()}
              disabled={submitting}
              className="text-sm text-gray-600 underline hover:text-gray-900 disabled:opacity-40"
            >
              Abbrechen (TAN-Vorgang verwerfen)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FintsTanChallengeModal;
